import { useState, useEffect, useCallback } from 'react';
import { CompanyListModal } from '../company';
import type { Company, TaxInvoiceModalProps, InvoiceData } from './types';
import { translations } from './constants';
import { TaxInvoiceHeader } from './TaxInvoiceHeader';
import { CompanySelector } from './CompanySelector';
import { OrderInfoSection } from './OrderInfoSection';
import { TaxDetailsSection } from './TaxDetailsSection';
import { TaxInvoiceActions } from './TaxInvoiceActions';
import { useLanguage } from '@/contexts/AppContext';
import { useTaxRate } from '@/contexts/StorePricingContext';

export function TaxInvoiceModal({
  isOpen,
  onClose,
  onComplete,
  orderData,
  selectedCompany: propSelectedCompany,
}: TaxInvoiceModalProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale } = useLanguage();
  // #285 stale 방지: 주문 데이터 스냅샷 우선, 없으면 현재 매장 컨텍스트 폴백
  const { taxRate: contextTaxRate } = useTaxRate();
  const taxRate = orderData.taxRate ?? contextTaxRate;
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCompanyList, setShowCompanyList] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (propSelectedCompany) {
      setSelectedCompany(propSelectedCompany);
    }
  }, [propSelectedCompany]);

  const t = translations[locale];

  const beforeTax = orderData.total / (1 + taxRate);
  const taxAmount = orderData.total - beforeTax;

  const handleCompanySelect = useCallback((company: Company) => {
    setSelectedCompany(company);
    setShowCompanyList(false);
  }, []);

  const handleShowCompanyList = useCallback(() => {
    setShowCompanyList(true);
  }, []);

  const handleCloseCompanyList = useCallback(() => {
    setShowCompanyList(false);
  }, []);

  const handleGenerateInvoice = useCallback(() => {
    if (!selectedCompany) return;

    setIsGenerating(true);

    setTimeout(() => {
      const invoiceData: InvoiceData = {
        folio: `CFDI${Date.now()}`,
        uuid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        company: selectedCompany,
        orderData,
        taxDetails: {
          beforeTax,
          taxAmount,
          taxRate,
          total: orderData.total
        },
        timestamp: new Date()
      };

      setIsGenerating(false);
      onComplete(invoiceData);
    }, 3000);
    // #522 lint: taxRate 누락 의존성 보강 (stale closure 방지 — 기존 경고 시정)
  }, [selectedCompany, orderData, beforeTax, taxAmount, taxRate, onComplete]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md mx-auto shadow-2xl max-h-[90vh] overflow-y-auto">
          <TaxInvoiceHeader
            t={t}
            onClose={onClose}
            isGenerating={isGenerating}
          />

          <div className="p-6 space-y-6">
            <CompanySelector
              company={selectedCompany}
              t={t}
              isGenerating={isGenerating}
              onShowCompanyList={handleShowCompanyList}
            />

            <OrderInfoSection
              orderData={orderData}
              t={t}
            />

            <TaxDetailsSection
              beforeTax={beforeTax}
              taxAmount={taxAmount}
              total={orderData.total}
              t={t}
              taxRate={orderData.taxRate}
              currency={orderData.currency}
            />

            <TaxInvoiceActions
              company={selectedCompany}
              isGenerating={isGenerating}
              t={t}
              onCancel={onClose}
              onGenerate={handleGenerateInvoice}
            />
          </div>
        </div>
      </div>

      <CompanyListModal
        isOpen={showCompanyList}
        onClose={handleCloseCompanyList}
        onSelectCompany={handleCompanySelect}
      />
    </>
  );
}
